import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Star, X } from 'lucide-react';
import { trackTagScroll } from '../utils/analytics';
import { ENTITY } from '../utils/constants';

const TagFilter = ({
  allTags,
  selectedTag,
  onSelectTag,
  featuredTags = [],
  hasActiveFilter = false,
  onClearFilters,
}) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollButtons();
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const prefersReducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const scroll = useCallback(
    (direction) => {
      if (scrollContainerRef.current) {
        const scrollAmount = 300;
        scrollContainerRef.current.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
        trackTagScroll(direction);
        requestAnimationFrame(updateScrollButtons);
      }
    },
    [updateScrollButtons, prefersReducedMotion]
  );

  const scrollLeft = useCallback(() => scroll('left'), [scroll]);
  const scrollRight = useCallback(() => scroll('right'), [scroll]);

  return (
    <div className="tags-container-wrapper">
      <button
        className="scroll-btn left"
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        aria-label={ENTITY.labels.scrollLeft}
      >
        <ChevronLeft size={18} suppressHydrationWarning />
      </button>
      <div className="tags-scroll" ref={scrollContainerRef}>
        {allTags.map((tag) => {
          const isFeatured = featuredTags.includes(tag);
          return (
            <button
              key={tag}
              className={[
                'tag-chip',
                selectedTag === tag ? 'active' : '',
                isFeatured ? 'featured' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelectTag(tag)}
            >
              {isFeatured && (
                <Star
                  size={14}
                  className="featured-icon"
                  fill="currentColor"
                  suppressHydrationWarning
                />
              )}
              {tag}
            </button>
          );
        })}
      </div>
      <button
        className="scroll-btn right"
        onClick={scrollRight}
        disabled={!canScrollRight}
        aria-label={ENTITY.labels.scrollRight}
      >
        <ChevronRight size={18} suppressHydrationWarning />
      </button>
      {hasActiveFilter && onClearFilters && (
        <button
          className="clear-filters-btn"
          onClick={onClearFilters}
          aria-label={ENTITY.labels.clearFilters}
        >
          <X size={14} suppressHydrationWarning />
          <span>{ENTITY.labels.clearFilters}</span>
        </button>
      )}
    </div>
  );
};

export default React.memo(TagFilter);
